import {
  fetchOrganizationSuccess,
  updateOrganizationSuccess,
} from 'client/modules/Organizations/OrganizationActions';
import moment from 'moment';
import { handleActions } from 'redux-actions';
import { twoFactorDisableSuccess, twoFactorEnableSuccess } from './TwoFactorActions';

const initialState = {
  isFetching: false,
  profile: null,
  errorMessage: null,
  nextPath: null,
  changingPasswordFor: null,
};

const updateOrganizationInProfile = (
  state,
  { payload: { organization } } = { payload: { organization: {} } },
) => {
  if (!state.profile) {
    return state;
  }
  const newOrgs = state.profile.organizations.map(o =>
    o._id === organization._id ? organization : o,
  );
  return {
    ...state,
    profile: { ...state.profile, organizations: newOrgs },
  };
};

const AuthReducer = handleActions(
  {
    REQUEST_LOGIN: state => ({
      ...state,
      isFetching: true,
    }),
    RECEIVE_LOGIN: (state, action) => {
      return {
        ...state,
        isFetching: false,
        errorMessage: null,
        profile: action.payload,
      };
    },
    LOGIN_ERROR: (state, action) => {
      return {
        ...state,
        isFetching: false,
        errorMessage: action.payload,
      };
    },
    RECEIVE_LOGOUT: (state, _action) => {
      return {
        ...state,
        isFetching: false,
        profile: null,
        nextPath: null,
      };
    },
    SAVE_NEXT_PATH: (state, action) => {
      return {
        ...state,
        nextPath: action.payload,
      };
    },
    SET_CHANGING_PASSWORD_FOR: (state, action) => {
      return {
        ...state,
        changingPasswordFor: action.payload,
      };
    },
    SET_CURRENT_ORGANIZATION: (state, action) => {
      if (!state.profile) {
        return state;
      }
      return {
        ...state,
        profile: { ...state.profile, organization: action.payload },
      };
    },
    CLEAR_ERROR_MESSAGE: state => {
      return {
        ...state,
        errorMessage: null,
      };
    },
    [fetchOrganizationSuccess]: updateOrganizationInProfile,
    [updateOrganizationSuccess]: updateOrganizationInProfile,
    PASSKEY_REGISTER_SUCCESS: state => {
      return {
        ...state,
        profile: { ...state.profile, hasPasskeys: true },
      };
    },
    PASSKEY_DELETE_SUCCESS: (state, { payload: { hasPasskeys } }) => {
      return {
        ...state,
        profile: { ...state.profile, hasPasskeys },
      };
    },
    [twoFactorEnableSuccess]: state => {
      return {
        ...state,
        profile: { ...state.profile, hasTwoFactorAuth: true },
      };
    },
    [twoFactorDisableSuccess]: state => {
      return {
        ...state,
        profile: { ...state.profile, hasTwoFactorAuth: false },
      };
    },
    ADD_SUBSCRIPTION_CB: (state, _action) => {
      return {
        ...state,
        profile: {
          ...state.profile,
          trialEndsAt: null,
        },
      };
    },
  },
  initialState,
);

export const isFetching = state => state.auth.isFetching;
export const getErrorMessage = state => state.auth.errorMessage;
export default AuthReducer;

export const getProfile = state => {
  return state.auth ? state.auth.profile : null;
};

export const getOrganization = state => {
  return state.auth?.profile?.organizations
    ? state.auth?.profile?.organizations.find(o => o._id === state.auth?.profile?.organization) ||
        state.auth?.profile?.organizations[0]
    : null;
};

export const getUserId = state => {
  return state.auth?.profile?.id || state.auth?.profile?.user_id || null;
};

export const getOrgRole = state => {
  return state.auth?.profile?.role || 'viewer';
};

export const canManageOrg = state => {
  return getOrgRole(state) === 'owner' || getOrgRole(state) === 'admin';
};

export const canWriteOrg = state => {
  return ['owner', 'admin', 'member'].includes(getOrgRole(state));
};

export const getUserWebId = state => {
  return state.auth && state.auth.profile ? state.auth.profile.webId : null;
};

export const getAllowSMS = state => hasFeature(state, 'sms');

export const getFeatures = state => {
  const profile = getProfile(state);
  return profile?.features || {};
};

export const hasFeature = (state, featureKey) => {
  const features = getFeatures(state);
  return features[featureKey] === true;
};

export const getTier = state => {
  const profile = getProfile(state);
  return profile?.tier || null;
};

export const getCurrentOrgId = state => {
  const org = state.auth?.profile?.organization;
  if (typeof org === 'string') {
    return org;
  }
  if (org && typeof org === 'object' && org._id) {
    return org._id.toString();
  }
  const fallbackOrgId = state.auth?.profile?.organizations?.[0]?._id;
  return fallbackOrgId ? fallbackOrgId.toString() : null;
};

export const isLoggedIn = state => {
  const profile = getProfile(state);
  // Check if user profile exists (tokens are now httpOnly cookies)
  // Profile presence indicates successful authentication
  return !!profile && !!(profile.id || profile.user_id);
};

export const getNextPath = state => {
  return state.auth ? state.auth.nextPath : null;
};

export const getChangingPasswordFor = state => state.auth && state.auth.changingPasswordFor;

export const getRemainingTrialDays = state => {
  const profile = getProfile(state);
  if (!profile) {
    return null;
  }
  const remaining = profile.trialEndsAt
    ? Math.max(moment(profile.trialEndsAt).endOf('day').diff(moment().startOf('day'), 'days'), 0)
    : null;
  return remaining;
};

export const visitorType = state => {
  if (isLoggedIn(state)) {
    if (getRemainingTrialDays(state) === null) {
      return 'customer';
    }
    return 'user';
  }
  return 'anonymous';
};
